import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  ChevronLeft, Camera, Check, Plus, X, CloudOff, RefreshCw, Loader2,
  ClipboardList, Image as ImageIcon, PencilRuler, IndianRupee, Boxes, CheckSquare, Package, WifiOff,
} from 'lucide-react';
import { enqueue, listQueue, onQueueChange, flush, retryEntry, removeEntry, clearSent, saveCache, readCache } from '@/lib/fieldQueue';

// ---------------------------------------------------------------------------
// Mobile field log — the same seven categories as the Telegram bot, built for
// one-handed phone use and installable to the home screen. Submissions are
// queued locally first so nothing waits on the network (site basements have no
// signal, and free hosting sleeps).
// ---------------------------------------------------------------------------

const CATS = {
  meeting:   { label: 'Meeting + MOM', icon: ClipboardList, color: '#3A8C8C' },
  photos:    { label: 'Photos',        icon: ImageIcon,     color: '#7DA68A' },
  drawings:  { label: 'Drawings',      icon: PencilRuler,   color: '#5B8DB8' },
  billing:   { label: 'Bills & Payments', icon: IndianRupee, color: '#C89B3C' },
  materials: { label: 'Materials',     icon: Boxes,         color: '#B0764F' },
  tasks:     { label: 'Tasks',         icon: CheckSquare,   color: '#7B6BA8' },
  orders:    { label: 'Orders',        icon: Package,       color: '#C4727F' },
};

const todayYmd = () => new Date().toISOString().slice(0, 10);

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

// Shrink phone photos before queueing — a 4 MB camera shot becomes ~300 KB,
// which keeps IndexedDB small and uploads fast on site connections.
async function compressImage(file, maxDim = 1600, quality = 0.72) {
  if (!file.type.startsWith('image/')) return { dataUrl: await readAsDataUrl(file), type: file.type };
  const dataUrl = await readAsDataUrl(file);
  const img = await new Promise((res, rej) => {
    const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  if (scale === 1 && file.size < 400 * 1024) return { dataUrl, type: file.type };
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return { dataUrl: canvas.toDataURL('image/jpeg', quality), type: 'image/jpeg' };
}

function fieldSpecs(cat, draft, boot) {
  const o = boot?.options || {};
  const members = [{ id: 'me', name: 'Assign to me' }, ...(boot?.members || [])];
  switch (cat) {
    case 'tasks': return [
      { k: 'title', label: 'Task title', kind: 'text' },
      { k: 'assigned', label: 'Assignee', kind: 'select', options: members.map(m => [m.id, m.name]) },
      { k: 'priority', label: 'Priority', kind: 'chips', options: (o.priorities || []).map(p => [p, p]) },
      { k: 'due_date', label: 'Due date', kind: 'date' },
      { k: 'task_type', label: 'Task type', kind: 'chips', options: (o.task_types || []).map(t => [t, t]) },
      { k: 'notes', label: 'Notes', kind: 'textarea', optional: true },
    ];
    case 'meeting': return [
      { k: 'date', label: 'Meeting date', kind: 'date' },
      { k: 'location', label: 'Location', kind: 'chips', options: o.locations || [] },
      { k: 'agenda', label: 'Agenda', kind: 'chips', options: o.agendas || [] },
      { k: 'attendees', label: 'Attendees', kind: 'multiadd' },
      { k: 'decisions', label: 'Decisions', kind: 'multiadd' },
      { k: 'actions', label: 'Action items', kind: 'multiadd' },
    ];
    case 'photos': return [
      { k: 'photo', label: 'Photo', kind: 'photo' },
      { k: 'caption', label: 'Caption', kind: 'text', optional: true },
    ];
    case 'drawings': return [
      { k: 'file', label: 'Drawing file or photo', kind: 'photo' },
      { k: 'title', label: 'Drawing title', kind: 'text' },
    ];
    case 'materials': return draft.materialMode === 'update' ? [
      { k: 'company', label: 'Company / brand', kind: 'text', optional: true },
      { k: 'item_code', label: 'Item code', kind: 'text', optional: true },
      { k: 'photo', label: 'Photo', kind: 'photo', optional: true },
    ] : [
      { k: 'company', label: 'Company / brand', kind: 'text' },
      { k: 'item_code', label: 'Item code', kind: 'text' },
      { k: 'photo', label: 'Material photo', kind: 'photo', optional: true },
    ];
    case 'billing': return draft.recordType === 'payment' ? [
      { k: 'date', label: 'Payment date', kind: 'date' },
      { k: 'amount', label: 'Payment amount (Rs)', kind: 'number' },
      { k: 'mode', label: 'Payment mode', kind: 'chips', options: (o.payment_modes || []).map(m => [m, m]) },
      { k: 'reference', label: 'Reference no.', kind: 'text', optional: true },
      { k: 'notes', label: 'Notes', kind: 'textarea', optional: true },
    ] : [
      { k: 'bill_number', label: 'Bill number', kind: 'text' },
      { k: 'bill_date', label: 'Bill date', kind: 'date' },
      { k: 'bill_amount', label: 'Amount (Rs)', kind: 'number' },
      { k: 'notes', label: 'Notes', kind: 'textarea', optional: true },
      { k: 'photo', label: 'Bill image', kind: 'photo', optional: true },
    ];
    case 'orders': return [
      { k: 'order_number', label: 'Order number', kind: 'text' },
      { k: 'item_description', label: 'Item description', kind: 'text' },
      { k: 'order_date', label: 'Order date', kind: 'date' },
      { k: 'tentative_delivery_date', label: 'Tentative delivery', kind: 'date', optional: true },
      { k: 'order_amount', label: 'Order amount (Rs)', kind: 'number' },
      { k: 'delivery_location', label: 'Delivery site', kind: 'text', optional: true },
      { k: 'status', label: 'Status', kind: 'chips', options: (o.order_statuses || []).map(s => [s, s]) },
      { k: 'notes', label: 'Notes', kind: 'textarea', optional: true },
    ];
    default: return [];
  }
}

// Sub-questions the bot asks before the fields (folder, vendor, bill, …).
function subSteps(cat, boot, project) {
  const vendors = (boot?.vendors || []).map(v => [v.id, v.name]);
  switch (cat) {
    case 'photos': return [{ k: 'folderId', label: 'Which folder?', options: (project?.photo_folders || []).map(f => [f.id, f.name]), allowNew: 'folderName' }];
    case 'drawings': return [{ k: 'folderName', label: 'Folder name', kind: 'text' }];
    case 'materials': return [
      { k: 'materialMode', label: 'New material or update an existing one?', options: [['new', 'Add new material'], ['update', 'Update existing']] },
      { k: 'materialName', label: 'Material name', kind: 'text', when: d => d.materialMode !== 'update' },
      { k: 'materialId', label: 'Which material?', options: (project?.materials || []).map(m => [m.id, m.item_name]), when: d => d.materialMode === 'update' },
      { k: 'vendorId', label: 'Vendor', options: vendors, optional: true },
    ];
    case 'billing': return [
      { k: 'recordType', label: 'New bill or a payment on an existing bill?', options: [['bill', 'New bill'], ['payment', 'Payment on existing bill']] },
      { k: 'billType', label: 'Bill type', options: (boot?.options?.bill_types || []).map(t => [t, t]), when: d => d.recordType !== 'payment' },
      { k: 'vendorId', label: 'Vendor', options: vendors, optional: true, when: d => d.recordType !== 'payment' },
      { k: 'billId', label: 'Which bill?', options: (project?.billing || []).map(b => [b.id, `${b.bill_number || 'Bill'} · ${b.vendor_name || ''} · Rs ${b.bill_amount || 0}`]), when: d => d.recordType === 'payment' },
    ];
    case 'orders': return [{ k: 'vendorId', label: 'Vendor', options: vendors, optional: true }];
    default: return [];
  }
}

export default function FieldLogPage() {
  const { user } = useAuth();
  const [boot, setBoot] = useState(null);
  const [offlineData, setOfflineData] = useState(false);
  const [step, setStep] = useState('project');   // project | category | sub | fields | done
  const [project, setProject] = useState(null);
  const [cat, setCat] = useState(null);
  const [draft, setDraft] = useState({});
  const [fields, setFields] = useState({});
  const [pending, setPending] = useState(0);
  const [queue, setQueue] = useState([]);
  const [showQueue, setShowQueue] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [lastSaved, setLastSaved] = useState(null);

  const refreshQueue = useCallback(async () => {
    const q = await listQueue();
    setQueue(q);
    setPending(q.filter(i => i.status !== 'sent').length);
  }, []);

  useEffect(() => {
    refreshQueue();
    const off = onQueueChange(refreshQueue);
    const on = () => { setOnline(true); flush(); };
    const offl = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', offl);
    return () => { off(); window.removeEventListener('online', on); window.removeEventListener('offline', offl); };
  }, [refreshQueue]);

  // Bootstrap: use the network when we have it, fall back to the last cache.
  useEffect(() => {
    (async () => {
      const cached = await readCache('bootstrap');
      if (cached) { setBoot(cached); setOfflineData(true); }
      try {
        const r = await api.get('/field-log/bootstrap');
        setBoot(r.data);
        setOfflineData(false);
        saveCache('bootstrap', r.data);
      } catch {
        if (!cached) toast.error('Could not load projects — connect once to set up offline use');
      }
    })();
    flush();
  }, []);

  const reset = () => { setStep('project'); setProject(null); setCat(null); setDraft({}); setFields({}); };

  const startCategory = (c) => {
    setCat(c);
    const steps = subSteps(c, boot, project).filter(s => !s.when || s.when({}));
    const seed = {};
    if (c === 'meeting') { seed.date = todayYmd(); seed.location = 'on_site'; }
    if (c === 'tasks') { seed.assigned = 'me'; seed.priority = 'Medium'; seed.task_type = 'General'; seed.due_date = todayYmd(); }
    if (c === 'billing') seed.bill_date = todayYmd();
    if (c === 'orders') { seed.order_date = todayYmd(); seed.status = 'Order Placed'; }
    if (c === 'photos') seed.caption = '';
    setFields(seed);
    setDraft({});
    setStep(steps.length ? 'sub' : 'fields');
  };

  const submit = async () => {
    const specs = fieldSpecs(cat, draft, boot);
    const missing = specs.filter(s => !s.optional && !hasValue(fields[s.k]));
    if (missing.length) { toast.error(`${missing[0].label} is required`); return; }

    // resolve vendor + material names so the record reads well offline too
    const v = (boot?.vendors || []).find(x => x.id === draft.vendorId);
    const finalDraft = {
      ...draft,
      ...(v ? { vendorName: v.name, vendorPhone: v.phone || '' } : {}),
    };
    await enqueue({
      category: cat,
      project_id: project.id,
      project_name: project.name,
      fields,
      draft: finalDraft,
    });
    setLastSaved({ cat, project: project.name, at: new Date() });
    setStep('done');
  };

  if (!boot) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F1F0EC]">
        <Loader2 className="animate-spin text-[#3A8C8C]" size={22} />
      </div>
    );
  }

  const cats = boot.categories || Object.keys(CATS);

  return (
    <div className="min-h-screen bg-[#F1F0EC] pb-24" data-testid="field-log-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-[#3A8C8C] text-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2">
          {step !== 'project' && (
            <button onClick={() => (step === 'fields' && subSteps(cat, boot, project).length ? setStep('sub') : step === 'done' ? reset() : step === 'sub' ? setStep('category') : setStep('project'))}
              className="-ml-1.5 p-1.5 rounded-lg hover:bg-white/15" data-testid="field-back">
              <ChevronLeft size={20} />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold font-['Manrope'] truncate">
              {step === 'project' ? 'Site Log' : project?.name || 'Site Log'}
            </p>
            <p className="text-[10px] text-white/70 truncate">
              {step === 'project' ? `Hello ${(user?.name || '').split(' ')[0] || ''} — pick a project`
                : cat ? CATS[cat]?.label : 'Choose what to log'}
            </p>
          </div>
          <button onClick={() => setShowQueue(true)} className="relative p-2 rounded-lg hover:bg-white/15" data-testid="field-queue-btn">
            {online ? <RefreshCw size={17} /> : <WifiOff size={17} />}
            {pending > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-[#C89B3C] text-[10px] font-bold flex items-center justify-center" data-testid="field-pending-count">
                {pending}
              </span>
            )}
          </button>
        </div>
        {!online && <p className="text-[10px] text-white/85 mt-1.5 flex items-center gap-1"><CloudOff size={11} /> Offline — entries save on your phone and upload later</p>}
        {online && offlineData && <p className="text-[10px] text-white/85 mt-1.5">Showing cached projects</p>}
      </div>

      <div className="px-4 pt-4 max-w-lg mx-auto">
        {step === 'project' && (
          <div className="space-y-2" data-testid="field-project-list">
            {(boot.projects || []).map(p => (
              <button key={p.id} onClick={() => { setProject(p); setStep('category'); }}
                className="w-full text-left bg-white rounded-xl px-4 py-3.5 shadow-sm active:scale-[0.99] transition-transform"
                data-testid={`field-project-${p.id}`}>
                <p className="text-sm font-semibold text-[#2C2C2E]">{p.name}</p>
                <p className="text-[11px] text-[#6B7280] mt-0.5">{p.client_name || '—'}</p>
              </button>
            ))}
            {!(boot.projects || []).length && <p className="text-sm text-[#6B7280] text-center py-10">No active projects.</p>}
          </div>
        )}

        {step === 'category' && (
          <div className="grid grid-cols-2 gap-2.5" data-testid="field-category-list">
            {cats.map(c => {
              const meta = CATS[c]; if (!meta) return null;
              const Icon = meta.icon;
              return (
                <button key={c} onClick={() => startCategory(c)}
                  className="bg-white rounded-xl p-4 shadow-sm flex flex-col items-center gap-2 active:scale-[0.98] transition-transform"
                  data-testid={`field-cat-${c}`}>
                  <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${meta.color}1A` }}>
                    <Icon size={20} strokeWidth={1.6} style={{ color: meta.color }} />
                  </span>
                  <span className="text-xs font-medium text-[#2C2C2E] text-center leading-tight">{meta.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {step === 'sub' && (
          <SubStepForm cat={cat} boot={boot} project={project} draft={draft} setDraft={setDraft}
            onDone={() => setStep('fields')} />
        )}

        {step === 'fields' && (
          <FieldForm specs={fieldSpecs(cat, draft, boot)} fields={fields} setFields={setFields} onSubmit={submit} />
        )}

        {step === 'done' && (
          <div className="text-center pt-10" data-testid="field-done">
            <div className="w-16 h-16 rounded-full bg-[#7DA68A]/15 flex items-center justify-center mx-auto">
              <Check size={30} className="text-[#7DA68A]" strokeWidth={2.4} />
            </div>
            <p className="text-base font-semibold text-[#2C2C2E] mt-4 font-['Manrope']">Saved on your phone</p>
            <p className="text-xs text-[#6B7280] mt-1.5 px-6">
              {CATS[lastSaved?.cat]?.label} for {lastSaved?.project}.{' '}
              {online ? 'Uploading now — you can carry on.' : 'It will upload when you get signal.'}
            </p>
            <div className="flex flex-col gap-2 mt-7">
              <button onClick={() => { setCat(null); setStep('category'); }}
                className="h-11 rounded-xl bg-[#3A8C8C] text-white text-sm font-medium" data-testid="field-log-another">
                Log another for this project
              </button>
              <button onClick={reset} className="h-11 rounded-xl bg-white text-[#2C2C2E] text-sm font-medium shadow-sm">
                Change project
              </button>
            </div>
          </div>
        )}
      </div>

      {showQueue && (
        <QueueSheet queue={queue} online={online} onClose={() => setShowQueue(false)} />
      )}
    </div>
  );
}

const hasValue = (v) => Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== null && String(v).trim() !== '');

// ── sub-context questions ───────────────────────────────
function SubStepForm({ cat, boot, project, draft, setDraft, onDone }) {
  const steps = subSteps(cat, boot, project).filter(s => !s.when || s.when(draft));
  const [newName, setNewName] = useState('');

  const ready = steps.every(s => s.optional || hasValue(draft[s.k]) || (s.allowNew && draft[s.allowNew]));

  return (
    <div className="space-y-4" data-testid="field-sub-form">
      {steps.map(s => (
        <div key={s.k} className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-xs font-semibold text-[#2C2C2E] mb-2.5">{s.label}{s.optional && <span className="text-[#9CA3AF] font-normal"> (optional)</span>}</p>
          {s.kind === 'text' ? (
            <input value={draft[s.k] || ''} onChange={e => setDraft({ ...draft, [s.k]: e.target.value })}
              className="w-full h-11 rounded-lg border border-[#D4D0C8] px-3 text-sm outline-none focus:border-[#3A8C8C]"
              data-testid={`field-sub-${s.k}`} />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(s.options || []).map(([val, label]) => (
                <button key={val} onClick={() => setDraft({ ...draft, [s.k]: val })}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${draft[s.k] === val ? 'bg-[#3A8C8C] text-white border-[#3A8C8C]' : 'bg-white text-[#2C2C2E] border-[#D4D0C8]'}`}
                  data-testid={`field-sub-${s.k}-${val}`}>
                  {label}
                </button>
              ))}
              {!(s.options || []).length && !s.allowNew && (
                <p className="text-[11px] text-[#C4727F]">Nothing available on this project yet.</p>
              )}
              {s.allowNew && (
                <div className="w-full mt-2 flex gap-1.5">
                  <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="or type a new folder name"
                    className="flex-1 h-10 rounded-lg border border-[#D4D0C8] px-3 text-sm outline-none focus:border-[#3A8C8C]" />
                  <button onClick={() => { if (newName.trim()) setDraft({ ...draft, [s.allowNew]: newName.trim(), [s.k]: '' }); }}
                    className="px-3 h-10 rounded-lg bg-[#E8E6E1] text-xs font-medium">Use</button>
                </div>
              )}
              {s.allowNew && draft[s.allowNew] && (
                <p className="w-full text-[11px] text-[#7DA68A] mt-1">New folder: {draft[s.allowNew]}</p>
              )}
            </div>
          )}
        </div>
      ))}
      <button onClick={onDone} disabled={!ready}
        className="w-full h-12 rounded-xl bg-[#3A8C8C] text-white text-sm font-semibold disabled:opacity-40"
        data-testid="field-sub-continue">
        Continue
      </button>
    </div>
  );
}

// ── main field form ─────────────────────────────────────
function FieldForm({ specs, fields, setFields, onSubmit }) {
  const set = (k, v) => setFields(prev => ({ ...prev, [k]: v }));
  return (
    <div className="space-y-3" data-testid="field-form">
      {specs.map(s => (
        <div key={s.k} className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-xs font-semibold text-[#2C2C2E] mb-2.5">
            {s.label}{s.optional && <span className="text-[#9CA3AF] font-normal"> (optional)</span>}
          </p>
          {s.kind === 'text' && (
            <input value={fields[s.k] || ''} onChange={e => set(s.k, e.target.value)}
              className="w-full h-11 rounded-lg border border-[#D4D0C8] px-3 text-sm outline-none focus:border-[#3A8C8C]"
              data-testid={`field-${s.k}`} />
          )}
          {s.kind === 'textarea' && (
            <textarea value={fields[s.k] || ''} onChange={e => set(s.k, e.target.value)} rows={3}
              className="w-full rounded-lg border border-[#D4D0C8] px-3 py-2 text-sm outline-none focus:border-[#3A8C8C] resize-none"
              data-testid={`field-${s.k}`} />
          )}
          {s.kind === 'number' && (
            <input type="number" inputMode="decimal" value={fields[s.k] ?? ''} onChange={e => set(s.k, e.target.value)}
              className="w-full h-11 rounded-lg border border-[#D4D0C8] px-3 text-sm outline-none focus:border-[#3A8C8C]"
              data-testid={`field-${s.k}`} />
          )}
          {s.kind === 'date' && (
            <input type="date" value={fields[s.k] || ''} onChange={e => set(s.k, e.target.value)}
              className="w-full h-11 rounded-lg border border-[#D4D0C8] px-3 text-sm outline-none focus:border-[#3A8C8C]"
              data-testid={`field-${s.k}`} />
          )}
          {s.kind === 'select' && (
            <select value={fields[s.k] || ''} onChange={e => set(s.k, e.target.value)}
              className="w-full h-11 rounded-lg border border-[#D4D0C8] px-2 text-sm bg-white outline-none focus:border-[#3A8C8C]"
              data-testid={`field-${s.k}`}>
              {(s.options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          )}
          {s.kind === 'chips' && (
            <div className="flex flex-wrap gap-1.5">
              {(s.options || []).map(([v, l]) => (
                <button key={v} onClick={() => set(s.k, v)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${fields[s.k] === v ? 'bg-[#3A8C8C] text-white border-[#3A8C8C]' : 'bg-white text-[#2C2C2E] border-[#D4D0C8]'}`}
                  data-testid={`field-${s.k}-${v}`}>{l}</button>
              ))}
            </div>
          )}
          {s.kind === 'multiadd' && <MultiAdd values={fields[s.k] || []} onChange={v => set(s.k, v)} name={s.k} />}
          {s.kind === 'photo' && <PhotoField value={fields[s.k]} onChange={v => set(s.k, v)} name={s.k} />}
        </div>
      ))}
      <button onClick={onSubmit} className="w-full h-12 rounded-xl bg-[#3A8C8C] text-white text-sm font-semibold shadow-sm active:scale-[0.99]"
        data-testid="field-submit">
        Save
      </button>
      <p className="text-[10px] text-[#9CA3AF] text-center pb-2">Saves to your phone instantly, uploads in the background.</p>
    </div>
  );
}

function MultiAdd({ values, onChange, name }) {
  const [text, setText] = useState('');
  const add = () => { const t = text.trim(); if (!t) return; onChange([...values, t]); setText(''); };
  return (
    <div>
      {values.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {values.map((v, i) => (
            <div key={i} className="flex items-start gap-2 bg-[#F1F0EC] rounded-lg px-3 py-2">
              <span className="flex-1 text-xs text-[#2C2C2E] leading-snug">{v}</span>
              <button onClick={() => onChange(values.filter((_, j) => j !== i))} className="text-[#C4727F] mt-0.5"><X size={13} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-1.5">
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          className="flex-1 h-11 rounded-lg border border-[#D4D0C8] px-3 text-sm outline-none focus:border-[#3A8C8C]"
          data-testid={`field-${name}-input`} />
        <button onClick={add} className="px-3.5 h-11 rounded-lg bg-[#E8E6E1] text-[#2C2C2E]" data-testid={`field-${name}-add`}>
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
}

function PhotoField({ value, onChange, name }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { dataUrl, type } = await compressImage(file);
      onChange({ name: file.name, type, size: dataUrl.length, data_url: dataUrl });
    } catch { toast.error('Could not read that file'); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <div>
      {value?.data_url ? (
        <div className="relative">
          {String(value.type || '').startsWith('image/')
            ? <img src={value.data_url} alt="attachment" className="w-full max-h-56 object-cover rounded-lg" />
            : <div className="h-16 rounded-lg bg-[#F1F0EC] flex items-center justify-center text-xs text-[#6B7280]">{value.name}</div>}
          <button onClick={() => onChange(null)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/55 text-white flex items-center justify-center" data-testid={`field-${name}-clear`}>
            <X size={14} />
          </button>
        </div>
      ) : (
        <button onClick={() => ref.current?.click()} disabled={busy}
          className="w-full h-24 rounded-lg border-2 border-dashed border-[#D4D0C8] flex flex-col items-center justify-center gap-1.5 text-[#6B7280]"
          data-testid={`field-${name}-pick`}>
          {busy ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} strokeWidth={1.6} />}
          <span className="text-xs">{busy ? 'Processing…' : 'Take photo or choose file'}</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*,application/pdf" capture="environment" onChange={pick} className="hidden" />
    </div>
  );
}

// ── sync queue sheet ────────────────────────────────────
function QueueSheet({ queue, online, onClose }) {
  const pending = queue.filter(q => q.status === 'pending');
  const failed = queue.filter(q => q.status === 'failed');
  const sent = queue.filter(q => q.status === 'sent');
  return (
    <div className="fixed inset-0 z-40 flex items-end" data-testid="field-queue-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-h-[80vh] overflow-y-auto bg-[#F1F0EC] rounded-t-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold font-['Manrope'] text-[#2C2C2E]">Sync</p>
            <p className="text-[11px] text-[#6B7280]">{online ? 'Online' : 'Offline — will upload when you reconnect'}</p>
          </div>
          <div className="flex gap-2">
            {online && <button onClick={() => flush()} className="px-3 h-8 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium" data-testid="field-sync-now">Sync now</button>}
            <button onClick={onClose} className="px-3 h-8 rounded-lg bg-white text-xs font-medium">Close</button>
          </div>
        </div>

        {[['Waiting to upload', pending], ['Needs attention', failed], ['Uploaded', sent]].map(([title, list]) => (
          list.length > 0 && (
            <div key={title} className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] uppercase tracking-wide text-[#6B7280] font-semibold">{title} ({list.length})</p>
                {title === 'Uploaded' && <button onClick={() => clearSent()} className="text-[10px] text-[#6B7280] hover:underline">Clear</button>}
              </div>
              <div className="space-y-1.5">
                {list.map(item => (
                  <div key={item.client_ref} className="bg-white rounded-lg px-3 py-2.5 shadow-sm">
                    <div className="flex items-center gap-2">
                      <span className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#2C2C2E] truncate">{CATS[item.category]?.label || item.category}</p>
                        <p className="text-[10px] text-[#6B7280] truncate">{item.project_name}</p>
                      </span>
                      {item.status === 'sent' && <Check size={14} className="text-[#7DA68A] shrink-0" />}
                      {item.status === 'pending' && <Loader2 size={13} className={`text-[#C89B3C] shrink-0 ${online ? 'animate-spin' : ''}`} />}
                      {item.status === 'failed' && (
                        <span className="flex gap-1.5 shrink-0">
                          <button onClick={() => retryEntry(item.client_ref)} className="text-[10px] text-[#3A8C8C] font-medium">Retry</button>
                          <button onClick={() => removeEntry(item.client_ref)} className="text-[10px] text-[#C4727F] font-medium">Discard</button>
                        </span>
                      )}
                    </div>
                    {item.error && <p className="text-[10px] text-[#C4727F] mt-1">{item.error}</p>}
                  </div>
                ))}
              </div>
            </div>
          )
        ))}

        {!queue.length && <p className="text-xs text-[#6B7280] text-center py-8">Nothing queued — everything is up to date.</p>}
      </div>
    </div>
  );
}
